import * as React from "react";
import { NavMenuOption, PageLayout } from "components/PageLayout/PageLayout";
import { useQueries, useQuery } from "react-query";
import { CognitoUserPoolType, InternalUser, UserAccountsService } from "gen/clients";
import { DataTable } from "components/DataTable/DataTable";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { Skeleton, TablePagination, TextField, Grid, Stack } from "@mui/material";
import { DataTableRowAction } from "components/DataTable/types";
import { useDialogState } from "hooks/useDialogState";
import { StaffAccountDetailsPanel } from "./StaffAccountDetailsPanel/StaffAccountDetailsPanel";
import { formatEpoch } from "../../utils/dateUtils";
import { useTranslation } from "react-i18next";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import { InfoIcon } from "../../components/InfoIcon/InfoIcon";

const StaffAccountsPage: React.FC = () => {
  const { t } = useTranslation();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [selectedUser, setSelectedUser] = React.useState<InternalUser | null>(null);
  const [isStaffAccountDetailsPanelOpen, openStaffAccountDetailsPanel, closeStaffAccountDetailsPanel] =
    useDialogState();

  const [filters, setFilters] = React.useState({ firstName: "", lastName: "", email: "", status: "ALL" });
  const [debouncedFilters, setDebouncedFilters] = React.useState(filters);

  // Debounce effect for filters
  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 1000); // 1-second debounce

    return () => {
      clearTimeout(handler);
    };
  }, [filters]);

  const handleFilterChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFilters(prevFilters => ({ ...prevFilters, [name]: value }));
    setPage(0);
  }, []);

  const {
    data: internalUserCollection,
    isLoading,
    error
  } = useQuery(["listInternalUsers", page, rowsPerPage, debouncedFilters], {
    queryFn: () =>
      UserAccountsService.listInternalUsers({
        limit: rowsPerPage,
        page: page + 1,
        firstName: debouncedFilters.firstName || undefined,
        lastName: debouncedFilters.lastName || undefined,
        email: debouncedFilters.email || undefined,
        status: debouncedFilters.status === "ALL" ? undefined : debouncedFilters.status
      })
  });

  const internalUsers = React.useMemo(() => internalUserCollection?.items || [], [internalUserCollection?.items]);

  const cognitoUserSummaryResults = useQueries(
    internalUsers.map(user => ({
      queryKey: ["listCognitoUsers", user.email],
      queryFn: () =>
        UserAccountsService.listCognitoUsers({ userPool: CognitoUserPoolType.INTERNAL_USER_POOL, email: user.email })
    }))
  );

  const cognitoUserDetailsResults = useQueries(
    cognitoUserSummaryResults.map(cognitoUserSummaryResult => ({
      enabled: !!cognitoUserSummaryResult.data?.items[0]?.username,
      queryKey: ["getCognitoUser", cognitoUserSummaryResult.data?.items[0]?.username],
      queryFn: () =>
        UserAccountsService.getCognitoUser({
          userPool: CognitoUserPoolType.INTERNAL_USER_POOL,
          // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
          cognitoUsername: cognitoUserSummaryResult.data?.items[0]?.username!
        })
    }))
  );

  const firstNameCell = React.useCallback((user: InternalUser) => user.firstName, []);

  const lastNameCell = React.useCallback((user: InternalUser) => user.lastName, []);

  const emailCell = React.useCallback((user: InternalUser) => user.email, []);

  const xtrfUserIdCell = React.useCallback((user: InternalUser) => user.xtrfUserId, []);

  const xtrfStatusCell = React.useCallback((user: InternalUser) => {
    switch (user.status) {
      case "ACTIVE":
        return "Active";
      case "DISABLED":
        return "Disabled";
      default:
        return "Unknown";
    }
  }, []);

  const userCognitoStatusCell = React.useCallback(
    (user: InternalUser) => {
      const index = internalUsers.findIndex(u => u.xtrfUserId === user.xtrfUserId);
      const { data: cognitoUser, isLoading: isCognitoUserLoading } = cognitoUserSummaryResults[index];
      if (isCognitoUserLoading) {
        return <Skeleton variant="rounded" animation="wave" />;
      }
      switch (cognitoUser?.items[0]?.enabled) {
        case true:
          return "Enabled";
        case false:
          return "Disabled";
        default:
          return "";
      }
    },
    [cognitoUserSummaryResults, internalUsers]
  );

  const rolesCell = React.useCallback(
    (user: InternalUser) => {
      const index = internalUsers.findIndex(u => u.xtrfUserId === user.xtrfUserId);
      const { data: cognitoUser, isLoading: isCognitoUserLoading } = cognitoUserDetailsResults[index];
      if (isCognitoUserLoading) {
        return <Skeleton variant="rounded" animation="wave" />;
      }
      return cognitoUser?.groups?.map(group => t(`roles.${group}`)).join(", ") || "";
    },
    [cognitoUserDetailsResults, internalUsers, t]
  );

  const mfaEnabledCell = React.useCallback(
    (user: InternalUser) => {
      const index = internalUsers.findIndex(u => u.xtrfUserId === user.xtrfUserId);
      const { data: cognitoUser, isLoading: isCognitoUserLoading } = cognitoUserDetailsResults[index];
      if (isCognitoUserLoading) {
        return <Skeleton variant="rounded" animation="wave" />;
      }
      if (!cognitoUser) {
        return "";
      }
      return cognitoUser.isMfaEnabled ? "Yes" : "No";
    },
    [cognitoUserDetailsResults, internalUsers]
  );

  const conflictsCell = React.useCallback(
    (user: InternalUser) => {
      const index = internalUsers.findIndex(u => u.xtrfUserId === user.xtrfUserId);
      const { data: cognitoUser, isLoading: isCognitoUserLoading } = cognitoUserDetailsResults[index];
      if (isCognitoUserLoading) {
        return <Skeleton variant="rounded" animation="wave" />;
      }
      if (!cognitoUser) {
        return "";
      }
      if (
        user.email !== cognitoUser?.email ||
        user.firstName !== cognitoUser?.firstName ||
        user.lastName !== cognitoUser?.lastName
      ) {
        return <WarningIcon color="error" />;
      } else {
        return <CheckCircleIcon color="success" />;
      }
    },
    [cognitoUserDetailsResults, internalUsers]
  );

  const lastVisitCell = React.useCallback(
    (user: InternalUser) => {
      const index = internalUsers.findIndex(u => u.xtrfUserId === user.xtrfUserId);
      const { data: cognitoUser, isLoading: isCognitoUserLoading } = cognitoUserDetailsResults[index];
      if (isCognitoUserLoading) {
        return <Skeleton variant="rounded" animation="wave" />;
      }
      return cognitoUser?.lastVisit ? formatEpoch(cognitoUser.lastVisit) : "";
    },
    [cognitoUserDetailsResults, internalUsers]
  );

  const rowKey = React.useCallback((user: InternalUser) => `${user.xtrfUserId}`, []);

  const rowActions = React.useCallback(
    (user: InternalUser): DataTableRowAction[] => [
      {
        title: "View Details",
        action: () => {
          setSelectedUser(user);
          openStaffAccountDetailsPanel();
        }
      }
    ],
    [openStaffAccountDetailsPanel]
  );

  const onRowClick = React.useCallback(
    (user: InternalUser) => {
      setSelectedUser(user);
      openStaffAccountDetailsPanel();
    },
    [openStaffAccountDetailsPanel]
  );

  const onCloseStaffAccountDetailsPanel = React.useCallback(() => {
    const userIndex = internalUsers.findIndex(u => u.xtrfUserId === selectedUser?.xtrfUserId);
    cognitoUserSummaryResults[userIndex]?.refetch();
    cognitoUserDetailsResults[userIndex]?.refetch();
    closeStaffAccountDetailsPanel();
  }, [
    closeStaffAccountDetailsPanel,
    cognitoUserDetailsResults,
    cognitoUserSummaryResults,
    internalUsers,
    selectedUser?.xtrfUserId
  ]);

  const onPageChange = React.useCallback((_, selectedPage: number) => {
    setPage(selectedPage);
  }, []);

  const onRowsPerPageChange = React.useCallback((event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setPage(0);
    setRowsPerPage(+event.target.value);
  }, []);

  return (
    <>
      <PageLayout selectedNavMenu={NavMenuOption.STAFF_ACCOUNTS} title="Staff Accounts">
        <Grid container spacing={2} marginBottom={2}>
          <Grid item xs={3}>
            <TextField
              name="firstName"
              label="First Name"
              value={filters.firstName}
              onChange={handleFilterChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              name="lastName"
              label="Last Name"
              value={filters.lastName}
              onChange={handleFilterChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={3}>
            <TextField name="email" label="Email" value={filters.email} onChange={handleFilterChange} fullWidth />
          </Grid>
          <Grid item xs={3}>
            <TextField
              select
              name="status"
              label="Status"
              value={filters.status}
              onChange={handleFilterChange}
              fullWidth
              SelectProps={{
                native: true
              }}
            >
              <option value="ALL">All</option>
              <option value="ACTIVE">Active</option>
              <option value="DISABLED">Disabled</option>
            </TextField>
          </Grid>
        </Grid>
        <DataTable
          columns={[
            { id: "firstName", title: "First Name", cell: firstNameCell },
            { id: "lastName", title: "Last Name", cell: lastNameCell },
            { id: "email", title: "Email", cell: emailCell },
            { id: "xtrfUserId", title: "XTRF ID", cell: xtrfUserIdCell },
            { id: "xtrfStatus", title: "XTRF Status", cell: xtrfStatusCell },
            { id: "cognitoStatus", title: "Cognito Status", cell: userCognitoStatusCell },
            { id: "roles", title: "Roles", cell: rolesCell },
            { id: "mfaEnabled", title: "MFA Enabled", cell: mfaEnabledCell },
            {
              id: "conflicts",
              title: (
                <Stack direction="row" spacing={1}>
                  <div>Conflicts</div>
                  <InfoIcon tooltip="Indicates if there are discrepencies between XTRF and Cognito user attributes in such values as the user's email, first name and last name." />
                </Stack>
              ),
              cell: conflictsCell
            },
            { id: "lastVisit", title: "Last Visit", cell: lastVisitCell }
          ]}
          data={internalUsers}
          rowKey={rowKey}
          isLoading={isLoading}
          error={!!error && <ApiErrorMessage apiError={error} />}
          rowActions={rowActions}
          onRowClick={onRowClick}
          dense={true}
        />
        {(internalUserCollection?.totalResults || 0) > 0 && (
          <TablePagination
            component="div"
            count={internalUserCollection?.totalResults || 0}
            page={page}
            onPageChange={onPageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={onRowsPerPageChange}
          />
        )}
      </PageLayout>

      {isStaffAccountDetailsPanelOpen && selectedUser && (
        <StaffAccountDetailsPanel user={selectedUser} onClose={onCloseStaffAccountDetailsPanel} />
      )}
    </>
  );
};

export { StaffAccountsPage };
